/**
 * /terms — Compliance Baseline v1.0 (STA-128, STA-129).
 *
 * Mirrors `docs/product/compliance/legal/v1.0/terms-of-service.md`.
 * Keep prose in sync when revising — the source-of-truth is the
 * markdown file; this page is the render target.
 */

import { LegalPage, Section } from '../components/LegalPage';

export default function Terms() {
  return (
    <LegalPage title="服務條款" version="v1.0" lastUpdated="2026-05-14">
      <Section title="第 1 條　簡介與適用範圍">
        <p>
          本服務條款（下稱「<strong>本條款</strong>」）係由 Starstwinkling
          （下稱「<strong>本站</strong>」或「<strong>我們</strong>」）所提供之線上塔羅占卜與
          AI 解牌服務（下稱「<strong>本服務</strong>」）之使用協議。
        </p>
        <p>
          當您完成註冊、勾選同意，或以任何方式使用本服務時，即視為您已閱讀、理解並同意本條款全部內容。
          若您不同意本條款任一部分，請立即停止使用本服務。
        </p>
      </Section>

      <Section title="第 2 條　用戶資格">
        <ul>
          <li><strong>年齡限制</strong>：本服務僅供 <strong>滿 18 歲</strong> 之自然人使用。未滿 18 歲者，須由法定代理人陪同並取得其同意後方可使用。</li>
          <li><strong>真實性</strong>：您承諾於註冊時提供之資訊為真實、準確且最新。</li>
          <li><strong>本人使用</strong>：您的帳號僅供本人使用，不得轉讓、出借或共用。</li>
        </ul>
      </Section>

      <Section title="第 3 條　服務內容">
        <ul>
          <li>本站提供以塔羅牌為基礎之 AI 解讀服務，內容包含但不限於：每日抽牌、牌陣占卜、AI 導師解牌、占卜紀錄管理。</li>
          <li>本服務屬<strong>個人心靈探索與娛樂用途</strong>，不構成醫療、法律、財務、投資、心理諮商或任何其他專業意見。詳見第 9 條「免責聲明」。</li>
          <li>我們保留隨時新增、修改、暫停或終止部分功能之權利，且不另行通知。</li>
        </ul>
      </Section>

      <Section title="第 4 條　帳號與安全">
        <ul>
          <li>您應自行妥善保管帳號密碼，不得告知第三人。</li>
          <li>任何透過您帳號發生之行為，均視為您本人之行為。</li>
          <li>如發現帳號遭盜用，應立即通知本站並配合調查。</li>
        </ul>
      </Section>

      <Section title="第 5 條　付費服務">
        <ul>
          <li>部分功能須付費始能使用，計價幣別為 <strong>新臺幣（NT$ / TWD）</strong>。</li>
          <li>完整付款流程、退費政策另見<a href="/refund-policy" className="text-tarot-gold underline">《退費政策》</a>。</li>
          <li>我們保留隨時調整價格與方案之權利，調整將於本站公告後生效，已成立之訂單不受影響。</li>
        </ul>
      </Section>

      <Section title="第 6 條　使用者行為規範">
        <p>您同意不為下列行為：</p>
        <ul>
          <li>散布違法、暴力、色情、歧視、騷擾或侵害他人權利之內容；</li>
          <li>干擾、破壞本服務之運作（含 DDoS、爬蟲、自動化攻擊）；</li>
          <li>嘗試解析、反向工程、複製本服務之原始碼或 AI 模型；</li>
          <li>利用本服務從事任何違法或違反公序良俗之行為；</li>
          <li>冒充他人或不實表示與他人之關係。</li>
        </ul>
        <p>違反前項規定者，本站得逕行暫停或終止其帳號，並保留法律追訴之權利。</p>
      </Section>

      <Section title="第 7 條　智慧財產權">
        <ul>
          <li>本站之 logo、視覺設計、UI、AI 解牌邏輯、塔羅牌面繪圖等，著作權及其他智慧財產權均屬本站或合法授權人所有。</li>
          <li>您於本服務中產生之占卜紀錄（含您的提問內容），其著作權歸您所有；但您授權本站於提供服務範圍內為必要之利用（如儲存、檢索、AI 解讀運算）。</li>
          <li>您不得將本站內容用於商業用途、再散布或公開展示，除非另獲書面同意。</li>
        </ul>
      </Section>

      <Section title="第 8 條　隱私權與資料保護">
        <p>
          您的個人資料處理方式，另見<a href="/privacy" className="text-tarot-gold underline">《隱私權政策》</a>。本站採用業界常見之加密與去識別化技術保護您的提問內容，但無法保證 100% 不受網路攻擊或第三方非法存取。
        </p>
      </Section>

      <Section title="第 9 條　免責聲明">
        <ul>
          <li>本服務提供之塔羅占卜與 AI 解牌結果，<strong>僅供個人心靈探索、娛樂與參考之用</strong>，不具備任何醫療、法律、財務投資或心理諮商之專業效力。</li>
          <li>占卜結果<strong>不代表絕對發生之未來</strong>。您於閱讀後所做之任何決策與行為，均由您自行承擔全部責任。</li>
          <li>若您正面臨重大決策（如就醫、投資、法律訴訟、心理危機），請務必尋求相關專業人士協助，<strong>勿以本服務取代專業意見</strong>。</li>
          <li>本站對因使用本服務而產生之任何直接、間接、附隨或衍生性損害，<strong>於法律允許之最大範圍內不負賠償責任</strong>。</li>
        </ul>
      </Section>

      <Section title="第 10 條　服務變更與中止">
        <ul>
          <li>本站保留隨時修改、暫停或永久終止本服務之權利。</li>
          <li>若因不可抗力（含但不限於：天災、戰爭、政府命令、第三方服務中斷）導致服務無法提供，本站不負賠償責任。</li>
        </ul>
      </Section>

      <Section title="第 11 條　條款變更">
        <ul>
          <li>本站得隨時修訂本條款，修訂後將於本站公告，並更新「最後更新日期」與版本號。</li>
          <li>重大變更將於生效前 7 日以電子郵件或站內信通知。</li>
          <li>若您於變更後繼續使用本服務，視為同意變更後之條款。</li>
        </ul>
      </Section>

      <Section title="第 12 條　準據法與管轄法院">
        <ul>
          <li>本條款之解釋與適用，以<strong>中華民國法律</strong>為準據法。</li>
          <li>因本條款所生之爭議，雙方同意以<strong>臺灣臺北地方法院</strong>為第一審管轄法院。</li>
        </ul>
      </Section>

      <Section title="第 13 條　聯絡方式">
        <p>如對本條款有任何疑問，請來信：📧 support@starstwinkling.com</p>
      </Section>
    </LegalPage>
  );
}
